import { find } from '~/utils';
import siteRouterManager, { SiteKey, SiteRouterKey } from '~/utils/site-router';
import { CUSTOM_AUTH_TYPE, URL_PARAMETER, type QueryKey } from '~/constants';
import {
    IPlugin,
    IPluginApplet,
    IPluginAuthTemplate,
    IPluginTrigger,
    PLUGIN_AUTH_TYPE,
    PLUGIN_RUNTIME,
    PluginArgumentField
} from '@fx-ui/open-sdk';
/**
 * 是否为后端函数
 *
 * @param symbol
 */
export function isPluginTriggerSymbol(symbol: string) {
    return /^((([a-z_]+\.)*([a-z_]+))|(func\.[0-9a-fA-F]{24}))$/.test(symbol);
}

/**
 * 是否为前端函数
 *
 * @param symbol
 */
export function isPluginAppletSymbol(symbol: string) {
    return /applet\.[0-9a-fA-F]{24}$/.test(symbol);
}

/**
 * 获取插件身份验证配置参数
 * @param authTemplateList
 * @param authentication
 */
export const getPluginAuthFields = (
    authTemplateList: IPluginAuthTemplate[] | undefined,
    authentication: IPlugin['authentication']
): PluginArgumentField[] | undefined => {
    const { type } = authentication || {};

    if (!type) {
        return;
    }

    if (CUSTOM_AUTH_TYPE.includes(type)) {
        // 参数配置读取插件配置
        return authentication?.conf_template?.fields;
    }
    // 参数配置读取模版
    return getPluginAuthFieldTemplate(authTemplateList, type)?.conf_template?.fields;
};

export const getPluginAuthFieldTemplate = (
    authTemplateList: IPluginAuthTemplate[] | undefined,
    authType: PLUGIN_AUTH_TYPE
): IPluginAuthTemplate | undefined => {
    return find(authTemplateList, (authTemplate) => authTemplate.auth_type === authType);
};

/**
 * 是否为 Python 语言族（官方 id 或自定义 id 均形如 python*）
 * @see fxp-open-sdk getFuncRuntime
 */
export const isPythonRuntime = (runtime?: PLUGIN_RUNTIME | string) => {
    return !!runtime && String(runtime).startsWith('python');
};

/**
 * 是否为 Node.js 语言族（官方 id 或自定义 id 均形如 nodejs*）
 * @see fxp-open-sdk getFuncRuntime
 */
export const isNodeJsRuntime = (runtime?: PLUGIN_RUNTIME | string) => {
    return !!runtime && String(runtime).startsWith('nodejs');
};

/** 编辑器/AI 用的语言标识 */
export const getPluginFnLang = (fn?: IPluginApplet | IPluginTrigger) => {
    const runtime = fn && 'runtime' in fn ? fn.runtime : undefined;
    return isPythonRuntime(runtime) ? 'python' : 'javascript';
};

/** 获取市场插件详情页链接 */
export const getStorePluginUrl = (pluginId: string, storeSource?: string) => {
    const query: { key: QueryKey; value: string | number }[] = [
        { key: URL_PARAMETER.PLUGIN_ID_SNAKE, value: pluginId }
    ];
    if (storeSource) {
        query.push({ key: URL_PARAMETER.PLUGIN_STORE_SOURCE, value: storeSource });
    }
    return siteRouterManager.getQueryAbsolutePath(
        query,
        SiteKey.Site,
        SiteRouterKey.OpenStorePlugin
    );
};

/**
 * 授权地址的查询参数里是否有 state。
 * 无法解析时返回 false，对齐 fx-open 对 oauth2_url 的判断
 */
export const hasOAuthStateQuery = (url = '') => {
    try {
        return new URL(url).searchParams.has('state');
    } catch {
        return false;
    }
};

/** 获取自建插件编辑页链接 */
export const getPluginEditUrl = (pluginId: string) => {
    return siteRouterManager.getQueryRelativePath(
        [{ key: URL_PARAMETER.PLUGIN_ID, value: pluginId }],
        SiteRouterKey.OpenPluginEdit
    );
};
